import React, { useState, useEffect, useMemo } from "react";
import Button, { BUTTON_DISPLAY } from "@acadia-ui/button";
import SideSheet, { Timeline } from "@acadia-ui/sidesheet";
import Status, { STATUS_TYPE, STATUS_DISPLAY } from "@acadia-ui/status";
import { ToastNotification, showToast, TOAST_TYPE } from "@acadia-ui/toast";
import { downloadCampaignExport, getCampaignDownloads } from "../../../Flux/Actions/MasterCampaignActions";
import "./campaignDownloadSidesheet.scss";

const DOWNLOAD_STATUS_MAPPING = {
	InProgress: {
		description: "Campaign download queued",
		comment: "The campaign file is not ready yet.",
		statusType: STATUS_TYPE.PENDING,
		statusTitle: "Queued"
	},
	InProgressWithError: {
		description: "Campaign download queued",
		comment: "The campaign file is not ready yet. Some campaigns with errors were excluded.",
		statusType: STATUS_TYPE.PENDING,
		statusTitle: "Queued"
	},
	Success: {
		description: "Campaign download succeeded",
		comment: "The campaign file was created successfully and is ready for download."
	},
	CompletedWithError: {
		description: "Campaign download succeeded",
		comment: "The campaign file was created successfully and is ready for download. Some campaigns with errors were excluded."
	},
	Downloaded: {
		description: "Campaign download completed",
		comment: "The campaign file has been downloaded."
	},
	DownloadedWithError: {
		description: "Campaign download completed",
		comment: "The campaign file has been downloaded. Some campaigns with errors were excluded."
	},
	Error: {
		description: "Campaign download failed",
		comment: "An error has occurred. Please submit another request.",
		statusType: STATUS_TYPE.ERROR,
		statusTitle: "Error"
	}
};
const STATUSES_WITH_BADGE = ["InProgress", "InProgressWithError", "Error"];
const STATUSES_WITH_DOWNLOAD = ["Success", "Downloaded", "CompletedWithError", "DownloadedWithError"];
const CAMPAIGN_DOWNLOAD_INTERVAL_MS = 30000;

export default () => {
	const [isOpen, setIsOpen] = useState(false);
	const [downloadCampaignRequests, setDownloadCampaignRequests] = useState([]);
	const [currentDownloadLogID, setCurrentDownloadLogID] = useState(null);

	useEffect(() => {
		refreshDownloads();
	}, []);

	const refreshDownloads = async () => {
		const campaignRequests = await getCampaignDownloads();
		// Check if there are any campaign export requests in progress and poll for the status of that export so we can update the UI when it completes.
		if (campaignRequests?.find((request) => request.Status === "InProgress" || request.Status === "InProgressWithError")) {
			setCurrentDownloadLogID(campaignRequests.find((request) => request.Status === "InProgress" || request.Status === "InProgressWithError").CampaignDownloadLogID);
		}
		setDownloadCampaignRequests(campaignRequests);
	};

	useEffect(() => {
		if (!currentDownloadLogID) return;
		const intervalId = setInterval(async () => {
			const campaignRequests = await getCampaignDownloads();
			const matchingRequest = campaignRequests?.find((request) => request.CampaignDownloadLogID === currentDownloadLogID);
			if (matchingRequest?.Status !== "InProgress" && matchingRequest?.Status !== "InProgressWithError") {
				setCurrentDownloadLogID(null);
				showToast(
					"masterCampaignToasts",
					<ToastNotification type={TOAST_TYPE.INFO} message="A campaign export has completed. Check the Campaign Download side sheet for updated status." />
				);
			}
			setDownloadCampaignRequests(campaignRequests);
		}, CAMPAIGN_DOWNLOAD_INTERVAL_MS);
		return () => {
			clearInterval(intervalId);
		};
	}, [currentDownloadLogID]);

	const formattedDownloadRequests = useMemo(() => {
		return downloadCampaignRequests.map((request) => {
			const formattedData = {
				eventDescription: DOWNLOAD_STATUS_MAPPING[request.Status].description.replace("Campaign", request.Filename || "Campaign"),
				comment: DOWNLOAD_STATUS_MAPPING[request.Status].comment,
				timestamp: request.LastUpdated
			};
			if (STATUSES_WITH_BADGE.includes(request.Status)) {
				formattedData.children = (
					<Status
						className="campaign-download-status"
						type={DOWNLOAD_STATUS_MAPPING[request.Status].statusType}
						title={DOWNLOAD_STATUS_MAPPING[request.Status].statusTitle}
					/>
				);
			}
			if (STATUSES_WITH_DOWNLOAD.includes(request.Status)) {
				formattedData.children = (
					<Button
						className="campaign-download-button"
						display={BUTTON_DISPLAY.SECONDARY}
						icon="icon-download"
						onClick={async () => {
							await downloadCampaignExport(request.CampaignDownloadLogID);
							setDownloadCampaignRequests(await getCampaignDownloads());
						}}
						data-tracking-id-alt="download-campaign-export"
					>
						Download
					</Button>
				);
			}
			return formattedData;
		});
	}, [downloadCampaignRequests]);

	return (
		<SideSheet className="campaign-download-sidesheet" headline="Campaign Download" isOpen={isOpen} onClose={() => setIsOpen(false)}>
			<Timeline data={formattedDownloadRequests} />
			<button id="refresh-downloads" onClick={refreshDownloads} data-tracking-id-alt="refresh-campaign-downloads">
				Refresh Downloads
			</button>
			<button id="open-sidesheet" onClick={() => setIsOpen(!isOpen)} data-tracking-id-alt="open-campaign-download-sidesheet">
				Open Sidesheet
			</button>
		</SideSheet>
	);
};
